import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link, useSearchParams } from 'react-router-dom';
import { useLocale } from '../../contexts/LocaleContext';
import { API_BASE } from '../../config';
import { Button } from '../ui/button';
import { RotatingDeleteIcon } from '../RotatingDeleteIcon';
import { AdminAccessDenied } from './AdminAccessDenied';
import { cn } from '../../lib/utils';
import {
  ADMIN_FILTER_BAR_CLASSNAME,
  ADMIN_INLINE_SELECT_CLASSNAME,
  ADMIN_MUTED_TEXT_CLASSNAME,
  ADMIN_PAGE_MEDIUM_CLASSNAME,
  AdminEmptyState,
  AdminErrorAlert,
  AdminLoadingState,
  AdminPaginationBar,
  AdminTableWrap,
} from './admin-ui';

type OnboardingKind = 'achievement' | 'reward';

interface AdminOnboardingEntry {
  kind: OnboardingKind;
  userId: string;
  userEmail: string | null;
  entryId: string;
  pointsAwarded: number | null;
  rewardPayload: unknown | null;
  createdAt: string;
  deletedAt: string | null;
}

type FilterUserOption = {
  id: string;
  email: string;
};

const ENTRIES_PER_PAGE = 25;

function entryKey(entry: AdminOnboardingEntry): string {
  return `${entry.kind}:${entry.userId}:${entry.entryId}`;
}

export const OnboardingAdmin: React.FC = () => {
  const { user, token, masterKey } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useLocale();
  const [entries, setEntries] = useState<AdminOnboardingEntry[]>([]);
  const [filterUsers, setFilterUsers] = useState<FilterUserOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [kindFilter, setKindFilter] = useState<'all' | OnboardingKind>('all');
  const [selectedUserId, setSelectedUserId] = useState(() => searchParams.get('userId') || '');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  const [restoringKey, setRestoringKey] = useState<string | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkRestoring, setBulkRestoring] = useState(false);

  const selectableEntries = useMemo(
    () => entries.filter((entry) => entry.deletedAt === null),
    [entries]
  );
  const restorableEntries = useMemo(
    () => entries.filter((entry) => entry.deletedAt !== null),
    [entries]
  );
  const selectableKeys = useMemo(
    () => selectableEntries.map((entry) => entryKey(entry)),
    [selectableEntries]
  );
  const allSelectableSelected =
    selectableKeys.length > 0 && selectableKeys.every((key) => selectedKeys.has(key));
  const selectedDeleteCount = useMemo(
    () => selectableEntries.filter((entry) => selectedKeys.has(entryKey(entry))).length,
    [selectableEntries, selectedKeys]
  );
  const selectedRestoreCount = useMemo(
    () => restorableEntries.filter((entry) => selectedKeys.has(entryKey(entry))).length,
    [restorableEntries, selectedKeys]
  );
  const bulkActionInProgress = bulkDeleting || bulkRestoring;

  const fetchEntries = useCallback(async (): Promise<void> => {
    if ((!token && !masterKey) || !user?.admin) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const params = new URLSearchParams({
        kind: kindFilter,
        page: String(currentPage),
        pageSize: String(ENTRIES_PER_PAGE),
        includeDeleted: String(includeDeleted),
      });
      if (selectedUserId) {
        params.set('userId', selectedUserId);
      }
      if (masterKey) {
        params.set('masterKey', masterKey);
      }
      const response = await fetch(`${API_BASE}/api/admin/onboarding?${params.toString()}`, {
        headers: {
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch onboarding entries');
      }
      const data = await response.json();
      setEntries(data.entries || []);
      setTotalPages(data.pagination?.totalPages || 1);
      setSelectedKeys(new Set());
      setError(null);
    } catch (err) {
      console.error('Error fetching onboarding entries:', err);
      setError('Failed to load onboarding entries');
    } finally {
      setLoading(false);
    }
  }, [token, masterKey, user?.admin, kindFilter, includeDeleted, currentPage, selectedUserId]);

  const fetchFilterUsers = useCallback(async (): Promise<void> => {
    if ((!token && !masterKey) || !user?.admin) {
      return;
    }
    try {
      const url = masterKey
        ? `${API_BASE}/api/users?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/users`;
      const response = await fetch(url, {
        headers: {
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) {
        return;
      }
      const data = await response.json();
      const users = Array.isArray(data.users) ? data.users : [];
      setFilterUsers(
        users
          .map((item: { id: string; email: string }) => ({
            id: item.id,
            email: item.email,
          }))
          .sort((left: FilterUserOption, right: FilterUserOption) =>
            left.email.localeCompare(right.email)
          )
      );
    } catch (err) {
      console.error('Error fetching users for onboarding filter:', err);
    }
  }, [token, masterKey, user?.admin]);

  useEffect(() => {
    void fetchEntries();
  }, [fetchEntries]);

  useEffect(() => {
    void fetchFilterUsers();
  }, [fetchFilterUsers]);

  const handleSelectedUserChange = (userId: string): void => {
    setSelectedUserId(userId);
    setCurrentPage(1);
    const next = new URLSearchParams(searchParams);
    if (userId) {
      next.set('userId', userId);
    } else {
      next.delete('userId');
    }
    setSearchParams(next, { replace: true });
  };

  const softDeletePath = (entry: AdminOnboardingEntry): string => {
    return entry.kind === 'achievement'
      ? `/api/admin/onboarding/achievements/${entry.userId}/${encodeURIComponent(entry.entryId)}`
      : `/api/admin/onboarding/rewards/${entry.userId}/${encodeURIComponent(entry.entryId)}`;
  };

  const restorePath = (entry: AdminOnboardingEntry): string => {
    return entry.kind === 'achievement'
      ? `/api/admin/onboarding/achievements/${entry.userId}/${encodeURIComponent(entry.entryId)}/restore`
      : `/api/admin/onboarding/rewards/${entry.userId}/${encodeURIComponent(entry.entryId)}/restore`;
  };

  const softDeleteRequest = async (entry: AdminOnboardingEntry): Promise<void> => {
    const path = softDeletePath(entry);
    const url = masterKey
      ? `${API_BASE}${path}?masterKey=${encodeURIComponent(masterKey)}`
      : `${API_BASE}${path}`;
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!response.ok) {
      throw new Error('Failed to delete entry');
    }
  };

  const restoreRequest = async (entry: AdminOnboardingEntry): Promise<void> => {
    const path = restorePath(entry);
    const url = masterKey
      ? `${API_BASE}${path}?masterKey=${encodeURIComponent(masterKey)}`
      : `${API_BASE}${path}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!response.ok) {
      throw new Error('Failed to restore entry');
    }
  };

  const softDelete = async (entry: AdminOnboardingEntry): Promise<void> => {
    if (!token && !masterKey) {
      return;
    }
    const key = entryKey(entry);
    setDeletingKey(key);
    try {
      await softDeleteRequest(entry);
      await fetchEntries();
    } catch (err) {
      console.error('Error deleting onboarding entry:', err);
      setError('Failed to delete entry');
    } finally {
      setDeletingKey(null);
    }
  };

  const restore = async (entry: AdminOnboardingEntry): Promise<void> => {
    if (!token && !masterKey) {
      return;
    }
    const key = entryKey(entry);
    setRestoringKey(key);
    try {
      await restoreRequest(entry);
      setSelectedKeys((previous) => {
        const next = new Set(previous);
        next.delete(key);
        return next;
      });
      await fetchEntries();
    } catch (err) {
      console.error('Error restoring onboarding entry:', err);
      setError('Failed to restore entry');
    } finally {
      setRestoringKey(null);
    }
  };

  const toggleSelection = (key: string): void => {
    setSelectedKeys((previous) => {
      const next = new Set(previous);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleSelectAll = (): void => {
    if (allSelectableSelected) {
      setSelectedKeys(new Set());
      return;
    }
    setSelectedKeys(new Set(selectableKeys));
  };

  const softDeleteSelected = async (): Promise<void> => {
    if ((!token && !masterKey) || selectedDeleteCount === 0) {
      return;
    }
    const toDelete = selectableEntries.filter((entry) => selectedKeys.has(entryKey(entry)));
    if (toDelete.length === 0) {
      return;
    }

    setBulkDeleting(true);
    try {
      const results = await Promise.allSettled(toDelete.map((entry) => softDeleteRequest(entry)));
      const failed = results.filter((result) => result.status === 'rejected').length;
      if (failed > 0) {
        setError(
          t('admin_onboarding_bulk_delete_partial_error').replace('{count}', String(failed))
        );
      }
      setSelectedKeys(new Set());
      await fetchEntries();
    } catch (err) {
      console.error('Error bulk deleting onboarding entries:', err);
      setError('Failed to delete selected entries');
    } finally {
      setBulkDeleting(false);
    }
  };

  const restoreSelected = async (): Promise<void> => {
    if ((!token && !masterKey) || selectedRestoreCount === 0) {
      return;
    }
    const toRestore = restorableEntries.filter((entry) => selectedKeys.has(entryKey(entry)));
    if (toRestore.length === 0) {
      return;
    }

    setBulkRestoring(true);
    try {
      const results = await Promise.allSettled(toRestore.map((entry) => restoreRequest(entry)));
      const failed = results.filter((result) => result.status === 'rejected').length;
      if (failed > 0) {
        setError(
          t('admin_onboarding_bulk_restore_partial_error').replace('{count}', String(failed))
        );
      }
      setSelectedKeys(new Set());
      await fetchEntries();
    } catch (err) {
      console.error('Error bulk restoring onboarding entries:', err);
      setError('Failed to restore selected entries');
    } finally {
      setBulkRestoring(false);
    }
  };

  if (!user || !user.admin) {
    return <AdminAccessDenied />;
  }

  return (
    <>
      <div className={ADMIN_PAGE_MEDIUM_CLASSNAME}>
        <div className={`${ADMIN_FILTER_BAR_CLASSNAME} mb-4 justify-start`}>
          <label className="flex items-center gap-2 text-sm text-foreground">
            <span className={ADMIN_MUTED_TEXT_CLASSNAME}>{t('filter_by_user')}:</span>
            <select
              value={selectedUserId}
              onChange={(e) => handleSelectedUserChange(e.target.value)}
              className={ADMIN_INLINE_SELECT_CLASSNAME}
            >
              <option value="">{t('all_users')}</option>
              {filterUsers.map((filterUser) => (
                <option key={filterUser.id} value={filterUser.id}>
                  {filterUser.email}
                </option>
              ))}
              {selectedUserId &&
                !filterUsers.some((filterUser) => filterUser.id === selectedUserId) && (
                  <option value={selectedUserId}>{selectedUserId}</option>
                )}
            </select>
          </label>
          <select
            value={kindFilter}
            onChange={(e) => {
              setKindFilter(e.target.value as 'all' | OnboardingKind);
              setCurrentPage(1);
            }}
            className={ADMIN_INLINE_SELECT_CLASSNAME}
          >
            <option value="all">{t('admin_onboarding_filter_all')}</option>
            <option value="achievement">{t('admin_onboarding_filter_achievements')}</option>
            <option value="reward">{t('admin_onboarding_filter_rewards')}</option>
          </select>
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => {
                setIncludeDeleted(e.target.checked);
                setCurrentPage(1);
              }}
            />
            {t('admin_onboarding_include_deleted')}
          </label>
          {selectedDeleteCount > 0 && (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              disabled={bulkActionInProgress}
              onClick={() => void softDeleteSelected()}
              className="group inline-flex items-center gap-1.5"
            >
              {!bulkDeleting ? <RotatingDeleteIcon /> : null}
              {bulkDeleting
                ? t('loading')
                : t('admin_onboarding_delete_selected').replace('{count}', String(selectedDeleteCount))}
            </Button>
          )}
          {selectedRestoreCount > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={bulkActionInProgress}
              onClick={() => void restoreSelected()}
              className="border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200 dark:hover:bg-emerald-900"
            >
              {bulkRestoring
                ? t('loading')
                : t('admin_onboarding_restore_selected').replace('{count}', String(selectedRestoreCount))}
            </Button>
          )}
        </div>

        {error && (
          <div className="mb-4">
            <AdminErrorAlert>{error}</AdminErrorAlert>
          </div>
        )}
        {loading ? (
          <AdminLoadingState />
        ) : entries.length === 0 ? (
          <AdminEmptyState>{t('admin_onboarding_empty')}</AdminEmptyState>
        ) : (
          <AdminTableWrap>
            <table className="admin-data-table">
              <thead>
                <tr>
                  <th className="w-10">
                    <input
                      type="checkbox"
                      aria-label={t('admin_onboarding_select_all')}
                      checked={allSelectableSelected}
                      disabled={selectableKeys.length === 0 || bulkActionInProgress}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th>{t('admin_onboarding_col_kind')}</th>
                  <th>{t('admin_onboarding_col_user')}</th>
                  <th>{t('admin_onboarding_col_id')}</th>
                  <th>{t('admin_onboarding_col_details')}</th>
                  <th>{t('admin_onboarding_col_date')}</th>
                  <th>{t('admin_onboarding_col_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => {
                  const rowKey = entryKey(entry);
                  const isDeleted = entry.deletedAt !== null;
                  const isRowSelectable = isDeleted ? includeDeleted : true;
                  const labelKey =
                    entry.kind === 'achievement'
                      ? `onboarding_action_${entry.entryId}`
                      : `onboarding_milestone_${entry.entryId}`;
                  return (
                    <tr
                      key={rowKey}
                      className={cn(
                        entry.deletedAt && 'opacity-55',
                        selectedKeys.has(rowKey) && 'bg-destructive/10',
                      )}
                    >
                      <td>
                        <input
                          type="checkbox"
                          aria-label={t('admin_onboarding_select_row')}
                          checked={selectedKeys.has(rowKey)}
                          disabled={!isRowSelectable || bulkActionInProgress}
                          onChange={() => toggleSelection(rowKey)}
                        />
                      </td>
                      <td>
                        {entry.kind === 'achievement'
                          ? t('admin_onboarding_kind_achievement')
                          : t('admin_onboarding_kind_reward')}
                        {entry.deletedAt ? ` (${t('admin_onboarding_deleted')})` : ''}
                      </td>
                      <td>
                        <Link to={`/admin/users/${entry.userId}`}>
                          {entry.userEmail || entry.userId}
                        </Link>
                      </td>
                      <td>
                        <div>{t(labelKey)}</div>
                        <div className="text-xs text-muted-foreground">{entry.entryId}</div>
                      </td>
                      <td className="admin-table-cell-muted">
                        {entry.kind === 'achievement'
                          ? `+${entry.pointsAwarded ?? 0} pts`
                          : JSON.stringify(entry.rewardPayload)}
                      </td>
                      <td>
                        {new Date(entry.createdAt).toLocaleString()}
                      </td>
                      <td>
                        {!isDeleted && (
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            disabled={deletingKey === rowKey || bulkActionInProgress}
                            onClick={() => void softDelete(entry)}
                            className="group inline-flex items-center gap-1.5"
                          >
                            <RotatingDeleteIcon />
                            {t('admin_onboarding_soft_delete')}
                          </Button>
                        )}
                        {isDeleted && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={restoringKey === rowKey || bulkActionInProgress}
                            onClick={() => void restore(entry)}
                            className="border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200 dark:hover:bg-emerald-900"
                          >
                            {t('admin_onboarding_restore')}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </AdminTableWrap>
        )}

        <AdminPaginationBar
          page={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>
    </>
  );
};
